/**
 * The body of a document quick preview. For the firm, a client document opens
 * in the Case IDE's viewers and editors (`FileViewer`), and every save is a
 * new version of it. A read-only preview shows a PDF or image, and anything
 * else as the PDF the server renders. A case file is fetched as a PDF from
 * its case.
 */
import { FileQuestion, Loader2 } from "lucide-react";
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode
} from "react";

import { PdfPreview } from "@/components/common/PdfPreview";
import { cn } from "@/lib/utils";
import { ForeignNationalDocumentsAPI } from "../../../api/foreignNationalDocumentsApi";
import { FilesystemAPI } from "../../caseFiles/api/filesystemApi";
import { FileViewer } from "../../caseIde/editor/FileViewer";
import { clientDocumentStore } from "../clientDocumentStore";
import { isImageDocument, isPdfDocument } from "../documentsModel";
import type { ForeignNationalDocument } from "../types";

export const PreviewNotice: FC<{ title?: string; message: string }> = ({
  title,
  message
}) => (
  <div className="flex h-full flex-col items-center justify-center gap-2 px-4 text-center">
    <FileQuestion className="size-8 text-fg-4" />
    {title ? (
      <span className="text-base font-medium text-foreground">{title}</span>
    ) : null}
    <span className="text-sm text-muted-foreground">{message}</span>
  </div>
);

/** A file that lives only in one of the client's cases. */
export type CaseFilePreviewSource = { individualId: string; nodeId: string };

type Props = {
  foreignNationalId: string;
  document: ForeignNationalDocument | null;
  /** Preview this case file (as a PDF) instead of the client document. */
  caseFile?: CaseFilePreviewSource | null;
  /** Skip fetching (e.g. while a drawer is closed). */
  enabled?: boolean;
  /**
   * Firm only: open the client document in the Case IDE's viewers and
   * editors, saving new versions. The portal previews read-only.
   */
  editable?: boolean;
  /** A save or restore changed the document (the host refetches its list). */
  onDocumentChange?: (document: ForeignNationalDocument) => void;
  /** An editor has unsaved changes. */
  onDirtyChange?: (dirty: boolean) => void;
  className?: string;
  /** Overlaid at the bottom right (e.g. a re-analyze button). */
  overlay?: ReactNode;
};

const ClientDocumentViewer: FC<{
  foreignNationalId: string;
  document: ForeignNationalDocument;
  onDocumentChange?: (document: ForeignNationalDocument) => void;
  onDirtyChange?: (dirty: boolean) => void;
}> = ({ foreignNationalId, document, onDocumentChange, onDirtyChange }) => {
  // What the last save or restore returned, until the host's copy catches up.
  const [latest, setLatest] = useState<ForeignNationalDocument | null>(null);
  useEffect(() => setLatest(null), [document]);
  const current =
    latest &&
    latest.foreign_national_document_id ===
      document.foreign_national_document_id
      ? latest
      : document;
  const onChangeRef = useRef(onDocumentChange);
  onChangeRef.current = onDocumentChange;
  const store = useMemo(
    () =>
      clientDocumentStore(foreignNationalId, current, {
        onChange: (updated) => {
          setLatest(updated);
          onChangeRef.current?.(updated);
        }
      }),
    [foreignNationalId, current]
  );
  return <FileViewer store={store} onDirtyChange={onDirtyChange} />;
};

export const DocumentQuickPreview: FC<Props> = ({
  foreignNationalId,
  document,
  caseFile = null,
  enabled = true,
  editable = false,
  onDocumentChange,
  onDirtyChange,
  className,
  overlay
}) => {
  const seqRef = useRef(0);
  const [docBlob, setDocBlob] = useState<Blob | null>(null);
  const [pdfBlob, setPdfBlob] = useState<Blob | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const docId = document?.foreign_national_document_id ?? "";
  const caseIndividualId = caseFile?.individualId ?? "";
  const caseNodeId = caseFile?.nodeId ?? "";
  const fromCase = Boolean(caseIndividualId && caseNodeId);
  const inEditor = editable && !fromCase && Boolean(document);
  // Read-only, anything but a PDF or an image is shown as the server's PDF.
  const viaPdf =
    !fromCase && !isPdfDocument(document) && !isImageDocument(document);

  useEffect(() => {
    setDocBlob(null);
    setPdfBlob(null);
    setError(null);
    if (!enabled || inEditor || (!fromCase && (!foreignNationalId || !docId))) {
      setIsLoading(false);
      return;
    }
    seqRef.current += 1;
    const seq = seqRef.current;
    setIsLoading(true);
    const load = fromCase
      ? FilesystemAPI.getNodePdf(caseIndividualId, caseNodeId).then((blob) => {
          if (seqRef.current === seq) setPdfBlob(blob);
        })
      : viaPdf
      ? ForeignNationalDocumentsAPI.getFilePdfBlob(
          foreignNationalId,
          docId
        ).then((blob) => {
          if (seqRef.current === seq) setPdfBlob(blob);
        })
      : ForeignNationalDocumentsAPI.getFileBlob(foreignNationalId, docId).then(
          (blob) => {
            if (seqRef.current === seq) setDocBlob(blob);
          }
        );
    load
      .catch(() => {
        if (seqRef.current !== seq) return;
        setError("Failed to load document preview.");
      })
      .finally(() => {
        if (seqRef.current === seq) setIsLoading(false);
      });
  }, [
    docId,
    foreignNationalId,
    enabled,
    inEditor,
    fromCase,
    viaPdf,
    caseIndividualId,
    caseNodeId
  ]);

  const blobUrl = useMemo(() => {
    if (typeof window === "undefined") return null;
    const blob = pdfBlob ?? docBlob;
    return blob ? URL.createObjectURL(blob) : null;
  }, [pdfBlob, docBlob]);

  useEffect(
    () => () => {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    },
    [blobUrl]
  );

  const isPdf = Boolean(pdfBlob) || isPdfDocument(document);
  const isImage = !pdfBlob && isImageDocument(document);
  const pdfUrl = isPdf ? blobUrl : null;
  const imageUrl = isImage ? blobUrl : null;
  const filename = document?.filename ?? "Untitled document";

  return (
    <div
      className={cn(
        "relative min-h-0 flex-1 overflow-hidden bg-fill-1",
        className
      )}
      data-testid="document-quick-preview"
    >
      {inEditor && document ? (
        enabled ? (
          <ClientDocumentViewer
            foreignNationalId={foreignNationalId}
            document={document}
            onDocumentChange={onDocumentChange}
            onDirtyChange={onDirtyChange}
          />
        ) : null
      ) : isLoading ? (
        <div className="flex h-full flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-5 animate-spin" />
          Loading preview...
        </div>
      ) : error ? (
        <PreviewNotice title="Preview unavailable" message={error} />
      ) : pdfUrl ? (
        <PdfPreview
          file={pdfUrl}
          ariaLabel={`${filename} preview`}
          defaultZoomMode="page-width"
          unavailableText="Failed to load document preview."
        />
      ) : imageUrl ? (
        <div className="flex h-full w-full items-center justify-center overflow-auto p-4">
          <img
            src={imageUrl}
            alt={filename}
            className="max-h-full max-w-full object-contain"
          />
        </div>
      ) : (
        <PreviewNotice message="No file available to preview." />
      )}
      {overlay ? (
        <div className="absolute right-6 bottom-6 z-20">{overlay}</div>
      ) : null}
    </div>
  );
};
